import type { Employee } from '../store/slices/employeeSlice';

const normalizeName = (name?: string | null) => (name || '').trim().toLowerCase();

export interface EmployeeHierarchyLookups {
  employeeById: Map<string, Employee>;
  employeeIdByUserId: Map<string, string>;
  employeeIdByName: Map<string, string>;
}

export const buildEmployeeHierarchyLookups = (employees: Employee[]): EmployeeHierarchyLookups => {
  const employeeById = new Map<string, Employee>();
  const employeeIdByUserId = new Map<string, string>();
  const employeeIdByName = new Map<string, string>();

  employees.forEach((employee) => {
    employeeById.set(employee._id, employee);

    if (employee.userId) {
      employeeIdByUserId.set(employee.userId, employee._id);
    }

    const nameKey = normalizeName(employee.name);
    if (nameKey && !employeeIdByName.has(nameKey)) {
      employeeIdByName.set(nameKey, employee._id);
    }
  });

  return { employeeById, employeeIdByUserId, employeeIdByName };
};

export const resolveManagerEmployeeId = (
  manager: {
    managerEmployeeId?: string | null;
    managerUserId?: string | null;
    managerName?: string | null;
  },
  lookups: EmployeeHierarchyLookups
): string | null => {
  const managerEmployeeId = manager.managerEmployeeId?.trim();
  if (managerEmployeeId) {
    if (lookups.employeeById.has(managerEmployeeId)) {
      return managerEmployeeId;
    }
  }

  const managerUserId = manager.managerUserId?.trim();
  if (managerUserId) {
    const managerIdFromUser = lookups.employeeIdByUserId.get(managerUserId);
    if (managerIdFromUser) {
      return managerIdFromUser;
    }
  }

  const managerNameKey = normalizeName(manager.managerName);
  return managerNameKey ? lookups.employeeIdByName.get(managerNameKey) || null : null;
};

export const getEffectiveManagerEmployeeId = (
  employee: Employee,
  lookups: EmployeeHierarchyLookups
): string | null => resolveManagerEmployeeId(
  {
    managerUserId: employee.managerUserId,
    managerName: employee.managerName,
  },
  lookups
);

export const isEmployeeDescendant = (
  ancestorId: string,
  targetId: string,
  lookups: EmployeeHierarchyLookups
): boolean => {
  let current = lookups.employeeById.get(targetId) || null;
  const seen = new Set<string>();

  while (current) {
    if (seen.has(current._id)) {
      return false;
    }

    seen.add(current._id);

    const managerEmployeeId = getEffectiveManagerEmployeeId(current, lookups);
    if (!managerEmployeeId) {
      return false;
    }

    if (managerEmployeeId === ancestorId) {
      return true;
    }

    current = lookups.employeeById.get(managerEmployeeId) || null;
  }

  return false;
};
